import { useState, useEffect } from 'react';
import { submitContactForm } from '../api/GlobalApi';
import { useSettings } from "../context/SettingsProvider";

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'general'
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { settings } = useSettings();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contactTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'support', label: 'Technical Support' },
    { value: 'business', label: 'Business Partnership' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'bug', label: 'Report a Bug' },
    { value: 'other', label: 'Other' }
  ];

  const contactInfo = [
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Email',
      value: `${settings?.contact_email}`,
      link: `${settings?.contact_email}`
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: 'Phone',
      value: `${settings?.contact_phone}`,
      link: `${settings?.contact_phone}`
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Office',
      value: `${settings?.business_address}, ${settings?.city}, ${settings?.state} ${settings?.zip_code}`,
      link: null
    },
    {
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Business Hours',
      value: 'Monday - Friday: 9:00 AM - 6:00 PM',
      link: null
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setSuccess(false);
    setErrorMessage('');
    
    try {
      const response = await submitContactForm(formData);
      
      if (response.data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          type: 'general'
        });
        
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || 
        'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="main-content">
      <article className="blog-article-header">
        <h1 className="blog-article-title">Contact Us</h1>
        <p className="blog-article-meta" style={{ marginTop: '12px' }}>
          Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>
      </article>

      <div style={styles.contactContainer}>
        <div style={styles.contactInfoGrid}>
          {contactInfo.map((info, index) => (
            <div key={index} style={styles.infoCard}>
              <div style={styles.infoIcon}>{info.icon}</div>
              <h3 style={styles.infoTitle}>{info.title}</h3>
              {info.link ? (
                <a href={info.link} style={styles.infoValue}>
                  {info.value}
                </a>
              ) : (
                <p style={styles.infoValue}>{info.value}</p>
              )}
            </div>
          ))}
        </div>

        <div style={styles.formSection}>
          <div style={styles.formContainer}>
            <div style={styles.formHeader}>
              <h2 style={styles.formTitle}>Send us a Message</h2>
              <p style={styles.formSubtitle}>
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
            </div>

            {success && (
              <div className="success-message">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Your message has been sent successfully! We'll get back to you soon.
              </div>
            )}

            {errorMessage && (
              <div className="error-message">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errorMessage}
              </div>
            )}

            <div style={styles.form}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={`form-input ${errors.name ? 'error' : ''}`}
                  />
                  {errors.name && <span className="form-error-text">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                  />
                  {errors.email && <span className="form-error-text">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    className={`form-input ${errors.subject ? 'error' : ''}`}
                  />
                  {errors.subject && <span className="form-error-text">{errors.subject}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Inquiry Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="form-input"
                  >
                    {contactTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your inquiry..."
                  rows="6"
                  className={`form-input ${errors.message ? 'error' : ''}`}
                  style={{ resize: 'vertical' }}
                />
                {errors.message && <span className="form-error-text">{errors.message}</span>}
                <span className="form-hint">{formData.message.length} characters</span>
              </div>

              <button
                onClick={handleSubmit}
                className={`auth-btn primary ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>

          <div style={styles.faqSection}>
            <div style={styles.faqCard}>
              <svg style={styles.faqIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 style={styles.faqTitle}>Check our FAQs</h3>
              <p style={styles.faqText}>
                You might find the answer to your question in our frequently asked questions section.
              </p>
              <a href="/faqs" style={styles.faqLink}>
                Browse FAQs →
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

const styles = {
  contactContainer: {
    marginTop: '32px',
  },
  contactInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
    marginBottom: '48px',
  },
  infoCard: {
    background: 'white',
    padding: '32px 24px',
    borderRadius: '12px',
    border: '1px solid #e8eaed',
    textAlign: 'center',
    transition: 'all 0.3s ease',
  },
  infoIcon: {
    width: '48px',
    height: '48px',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '12px',
    color: 'white',
  },
  infoTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '8px',
  },
  infoValue: {
    fontSize: '14px',
    color: '#5f6368',
    lineHeight: '1.6',
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
  formSection: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '32px',
  },
  formContainer: {
    background: 'white',
    padding: '40px',
    borderRadius: '12px',
    border: '1px solid #e8eaed',
  },
  formHeader: {
    marginBottom: '32px',
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '8px',
  },
  formSubtitle: {
    fontSize: '14px',
    color: '#5f6368',
    lineHeight: '1.6',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  faqSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  faqCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '32px',
    borderRadius: '12px',
    color: 'white',
    textAlign: 'center',
  },
  faqIcon: {
    width: '48px',
    height: '48px',
    margin: '0 auto 16px',
  },
  faqTitle: {
    fontSize: '20px',
    fontWeight: '500',
    marginBottom: '12px',
  },
  faqText: {
    fontSize: '14px',
    opacity: '0.9',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  faqLink: {
    display: 'inline-block',
    color: 'white',
    textDecoration: 'none',
    fontWeight: '500',
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '20px',
    transition: 'all 0.2s',
  },
};

const mediaStyles = document.createElement('style');
mediaStyles.innerHTML = `
  @media (max-width: 768px) {
    .form-section {
      grid-template-columns: 1fr !important;
    }
    
    .form-container {
      padding: 24px !important;
    }
  }
  
  a[style*="infoValue"]:hover {
    color: #1a73e8 !important;
    text-decoration: underline !important;
  }
  
  .info-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
    transform: translateY(-2px) !important;
  }
  
  a[style*="faqLink"]:hover {
    background: rgba(255,255,255,0.3) !important;
  }
`;
document.head.appendChild(mediaStyles);

export default ContactPage;